import { createTheme } from "@mui/material/styles";

// Brand palette extension — keys mirror the names in app/globals.css @theme block
// so theme consumers can refer to the same vocabulary as Tailwind utility classes.
declare module "@mui/material/styles" {
  interface Palette {
    sanctions: {
      black: string;
      green100: string;
      green200: string;
      green300: string;
      green400: string;
      green500: string;
      green600: string;
      green700: string;
      green900: string;
      greenishGrey200: string;
      yellow100: string;
      yellow300: string;
      yellow500: string;
      yellow700: string;
      red100: string;
      red400: string;
      red500: string;
      red600: string;
      // Alpha overlays (hex8: AA = round(opacity * 255))
      grey900_24: string;
      grey500_32: string;
      green500_12: string;
    };
  }
  interface PaletteOptions {
    sanctions?: Palette["sanctions"];
  }

  interface Theme {
    sanctions: {
      shadows: {
        flat: string;
        flatBlack: string;
        flatGreen: string;
        flatRed: string;
        soft: string;
        softBlack: string;
        glow: string;
      };
      blur: {
        bg: string;
      };
    };
  }
  interface ThemeOptions {
    sanctions?: Theme["sanctions"];
  }
}

export const theme = createTheme({
  typography: {
    fontFamily: '"Inter", system-ui, -apple-system, sans-serif',
    fontSize: 16,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: { fontSize: "48px", lineHeight: "56px", fontWeight: 700 },
    h2: { fontSize: "32px", lineHeight: "40px", fontWeight: 700 },
    h3: { fontSize: "24px", lineHeight: "32px", fontWeight: 700 },
    h4: { fontSize: "20px", lineHeight: "24px", fontWeight: 600 },
    body1: { fontSize: "16px", lineHeight: "24px", fontWeight: 400, letterSpacing: "-0.01em" },
    body2: { fontSize: "14px", lineHeight: "20px", fontWeight: 400, letterSpacing: "-0.01em" },
    caption: { fontSize: "12px", lineHeight: "16px", fontWeight: 400, letterSpacing: "-0.01em" },
    button: { textTransform: "none", fontWeight: 500 },
  },
  palette: {
    mode: "light",
    primary: {
      main: "#95bc0e", // green-500
      light: "#aad21f", // green-400
      dark: "#88ad0c", // green-600
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#2d343c", // black (brand "tertiary" button)
      contrastText: "#ffffff",
    },
    error: {
      main: "#f74d4d", // red-600
      light: "#ff7e7e", // red-400
      dark: "#ff6969", // red-500
      contrastText: "#ffffff",
    },
    warning: {
      main: "#fc9c0c", // yellow-700
      light: "#ffd680", // yellow-300
      dark: "#f6b733", // yellow-500
    },
    background: {
      default: "#ffffff",
      paper: "#ffffff",
    },
    text: {
      primary: "#2d343c", // black
      secondary: "#4e5966", // grey-900
      disabled: "#a7b0ba", // grey-500
    },
    divider: "#e5ebf1", // grey-200
    // MUI's grey scale — mapped to our custom grey tokens by visual proximity.
    grey: {
      50: "#f6f8fa",
      100: "#f2f5f9",
      200: "#e5ebf1",
      300: "#d9e0e8",
      400: "#a7b0ba", // mapped to grey-500 (closest in our scale)
      500: "#a7b0ba",
      600: "#73808e",
      700: "#4e5966", // mapped to grey-900
      800: "#384049", // grey-950
      900: "#2d343c", // black
    },
    sanctions: {
      black: "#2d343c",
      green100: "#fbffef",
      green200: "#f4fdd3",
      green300: "#d6fb55",
      green400: "#aad21f",
      green500: "#95bc0e",
      green600: "#88ad0c",
      green700: "#759507",
      green900: "#405200",
      greenishGrey200: "#e8ecda",
      yellow100: "#fff2d7",
      yellow300: "#ffd680",
      yellow500: "#f6b733",
      yellow700: "#fc9c0c",
      red100: "#fff2f2",
      red400: "#ff7e7e",
      red500: "#ff6969",
      red600: "#f74d4d",
      grey900_24: "#4e59663d",
      grey500_32: "#a7b0ba52",
      green500_12: "#95bc0e1f",
    },
  },
  shape: { borderRadius: 4 },
  sanctions: {
    shadows: {
      flat: "0px 1px 3px #181d2214", // #181D22 @ 8%
      flatBlack: "0px 1px 3px #2d343c52", // #2D343C @ 32%
      flatGreen: "0px 1px 3px #75950752", // #759507 @ 32%
      flatRed: "0px 1px 3px #f74d4d52", // #F74D4D @ 32%
      soft: "0px 8px 32px #2d343c14", // #2D343C @ 8%
      softBlack: "0px 8px 32px #181d227a", // #181D22 @ 48%
      glow: "2px 0px 12px #deff6a",
    },
    blur: {
      bg: "32px",
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          border: "1px solid #e5ebf1",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          borderRadius: 4,
        },
      },
    },
  },
});
